/**
 * Batch Chunker -- chia segments thanh batches theo token limit
 * va merge ket qua tra ve tu nhieu batches.
 */

const BATCH_TOKEN_LIMIT = 25_000;

/** Uoc tinh so token tu text (1 token ~3.5 chars) */
const estimateTokens = (text: string) => Math.ceil(text.length / 3.5);

export interface BatchChunk {
  startIdx: number;
  segments: string[];
}

/**
 * Chia segments thanh batches BALANCED dua tren token limit.
 * Moi segment LA mot paragraph hoan chinh (worker parse output) — KHONG bao gio cat giua paragraph.
 *
 * Balanced strategy:
 *   1. Tinh tong tokens
 *   2. N = ceil(total / limit)  → so batch toi uu
 *   3. Target per batch = total / N
 *   4. Greedy fill den khi dat target → close batch (boundary luon o segment break)
 *
 * Tranh tinh trang last batch tiny (vd 30K → [25K, 5K]) → balanced [15K, 15K].
 */
export function chunkSegments(
  segments: string[],
  tokenLimit = BATCH_TOKEN_LIMIT
): BatchChunk[] {
  if (segments.length === 0) return [];

  // Step 1: total tokens
  const segTokensList = segments.map(estimateTokens);
  const totalTokens = segTokensList.reduce((s, t) => s + t, 0);

  // Step 2: N optimal — ceil(total / limit), at least 1
  const N = Math.max(1, Math.ceil(totalTokens / tokenLimit));

  // Step 3: target per batch
  const targetPerBatch = totalTokens / N;

  // Step 4: greedy fill, close batch khi reach target (only at segment boundary)
  const batches: BatchChunk[] = [];
  let currentBatch: string[] = [];
  let batchStartIdx = 0;
  let currentTokens = 0;

  for (let i = 0; i < segments.length; i++) {
    const segTokens = segTokensList[i];

    // Close current batch khi:
    //  - Da reach target tokens (balanced)
    //  - HOAC them seg nay se vuot hard limit
    //  - VA con batch sau (luon de last batch fill remaining)
    //  - VA current batch khong empty (avoid empty batch)
    const reachedTarget = currentTokens >= targetPerBatch;
    const wouldExceedLimit = currentTokens + segTokens > tokenLimit;
    const hasMoreBatches = batches.length < N - 1;

    if ((reachedTarget || wouldExceedLimit) && hasMoreBatches && currentBatch.length > 0) {
      batches.push({ startIdx: batchStartIdx, segments: [...currentBatch] });
      currentBatch = [];
      batchStartIdx = i;
      currentTokens = 0;
    }

    currentBatch.push(segments[i]);
    currentTokens += segTokens;
  }

  if (currentBatch.length > 0) {
    batches.push({ startIdx: batchStartIdx, segments: currentBatch });
  }

  return batches;
}

/**
 * Smart chunker metadata (US-17-08) — per paragraph info to avoid "vô duyên" cuts.
 *
 * `segIdxStart` and `segIdxEnd` are inclusive indexes into the same `segments`
 * array passed to `chunkSegmentsSmart`. They define the contiguous range of
 * segments belonging to one source paragraph.
 */
export interface ParagraphMeta {
  segIdxStart: number;
  segIdxEnd: number;
  headingLevel?: number;
  inTable?: boolean;
  inDrawing?: boolean;
  pStyle?: string;
}

export interface ChunkParagraphSource {
  groups: Array<{ aiIdx?: number }>;
  headingLevel?: number;
  inTable?: boolean;
  inDrawing?: boolean;
  pStyle?: string;
}

export interface ChunkParseStructureSource {
  chartAiStart?: number;
  chartLabels?: string[];
  smartAiStart?: number;
  smartLabels?: string[];
  extraParts?: Array<{
    paragraphs?: Array<{ segMap?: Array<{ aiIdx?: number }> }>;
  }>;
}

/**
 * Build full segment-boundary metadata without changing what the review UI renders.
 * Body/textbox ranges come from ParagraphDisplay; chart/SmartArt and extra-part
 * ranges come from ParseStructure because those segments are translated/exported
 * but are intentionally absent from the paragraph review table.
 */
export function buildCompleteParagraphMeta(
  paragraphs: ChunkParagraphSource[],
  totalSegments: number,
  structure?: ChunkParseStructureSource,
): { meta: ParagraphMeta[]; aligned: boolean; reason?: string } {
  const meta: ParagraphMeta[] = [];
  const seen = new Set<number>();
  let duplicate = false;
  let nonContiguous = false;

  const addRange = (
    aiIdxs: number[],
    attrs: Omit<ParagraphMeta, 'segIdxStart' | 'segIdxEnd'> = {},
  ) => {
    if (aiIdxs.length === 0) return;
    const sorted = [...aiIdxs].sort((a, b) => a - b);
    const start = sorted[0];
    const end = sorted[sorted.length - 1];
    if (end - start + 1 !== sorted.length) nonContiguous = true;
    for (const idx of sorted) {
      if (seen.has(idx)) duplicate = true;
      seen.add(idx);
    }
    meta.push({ segIdxStart: start, segIdxEnd: end, ...attrs });
  };

  for (const paragraph of paragraphs) {
    addRange(
      paragraph.groups.flatMap(group => group.aiIdx === undefined ? [] : [group.aiIdx]),
      {
        headingLevel: paragraph.headingLevel,
        inTable: paragraph.inTable,
        inDrawing: paragraph.inDrawing,
        pStyle: paragraph.pStyle,
      },
    );
  }

  // These labels are translated and exported, but are not paragraph rows in the UI.
  // Keep each subsystem as an atomic range so a batch cannot split its label context.
  const chartCount = structure?.chartLabels?.length || 0;
  if (chartCount > 0 && structure?.chartAiStart !== undefined) {
    addRange(Array.from({ length: chartCount }, (_, i) => structure.chartAiStart! + i), { inDrawing: true });
  }
  const smartCount = structure?.smartLabels?.length || 0;
  if (smartCount > 0 && structure?.smartAiStart !== undefined) {
    addRange(Array.from({ length: smartCount }, (_, i) => structure.smartAiStart! + i), { inDrawing: true });
  }

  // Header/footer/footnote/endnote paragraphs may also contain multiple run groups.
  for (const part of structure?.extraParts || []) {
    for (const paragraph of part.paragraphs || []) {
      addRange((paragraph.segMap || []).flatMap(group => group.aiIdx === undefined ? [] : [group.aiIdx]));
    }
  }

  if (duplicate) return { meta, aligned: false, reason: 'duplicate' };
  if (nonContiguous) return { meta, aligned: false, reason: 'paragraph-non-contiguous' };
  const ids = [...seen].sort((a, b) => a - b);
  if (ids.length === 0) return { meta, aligned: false, reason: 'empty' };
  if (ids[0] !== 0 || ids[ids.length - 1] !== ids.length - 1) {
    return { meta, aligned: false, reason: 'gap' };
  }
  if (ids.length !== totalSegments) {
    return { meta, aligned: false, reason: `length-mismatch ${ids.length}/${totalSegments}` };
  }
  if (meta.some(range => range.segIdxStart < 0 || range.segIdxEnd >= totalSegments)) {
    return { meta, aligned: false, reason: 'invalid-range' };
  }
  return { meta, aligned: true };
}

/**
 * Mutating helper — assigns `listStreakId` to consecutive paragraphs whose
 * pStyle === 'ListParagraph'. All paragraphs in the same streak share the same
 * id (the index of the streak start). Non-list paragraphs get `undefined`.
 */
function markListStreaks(meta: ParagraphMeta[]): (number | undefined)[] {
  const ids: (number | undefined)[] = new Array(meta.length).fill(undefined);
  let inStreak = false;
  let streakStart = -1;
  for (let i = 0; i < meta.length; i++) {
    if (meta[i].pStyle === 'ListParagraph') {
      if (!inStreak) { inStreak = true; streakStart = i; }
      ids[i] = streakStart;
    } else {
      inStreak = false;
      streakStart = -1;
    }
  }
  return ids;
}

/**
 * Returns the paragraph index that contains segment `segIdx`. Built once per
 * chunker call as a flat lookup array `segIdx -> paraIdx` for O(1) access.
 */
function buildSegToParaMap(meta: ParagraphMeta[], totalSegments: number): number[] {
  const map = new Array(totalSegments).fill(-1);
  for (let pi = 0; pi < meta.length; pi++) {
    const m = meta[pi];
    for (let si = m.segIdxStart; si <= m.segIdxEnd && si < totalSegments; si++) {
      map[si] = pi;
    }
  }
  return map;
}

/**
 * Smart chunker — balanced fill PLUS skip "bad cuts" using paragraph metadata.
 *
 * A cut at segment boundary `i` (split between segment `i-1` and `i`) is "bad" if:
 *   0. Same paragraph: `prevPara === nextPara` — cut would split paragraph internally
 *   1. Heading orphan: prev paragraph ends with a heading (heading isolated at end)
 *   2. Mid-table: BOTH prev AND next paragraphs are inTable (cut truly splits table interior)
 *   3. Mid-drawing: BOTH prev AND next paragraphs are inDrawing (cut splits drawing/SmartArt/textbox)
 *   4. Mid-list: prev AND next belong to the SAME consecutive ListParagraph streak
 *
 * Cuts AT table/drawing boundaries (entering or exiting) are allowed — only cuts
 * truly inside a table/drawing block are bad. This matches the Stage 0 acceptance
 * for TC-3 (cut at i=3 or i=8 acceptable for table[3..7]).
 *
 * Trade-off accepted: 2 separate adjacent drawing/table blocks cannot be cut between
 * each other (no drawingId/tableId tracking V1). Cost = slight imbalance, not correctness.
 *
 * Edge case: if no safe cut found before reaching tokenLimit OR end of segments,
 * returns whatever is accumulated. May produce fewer batches than target N or
 * a batch that exceeds limit (acceptable per Gemini 1M context tolerance).
 */
export function chunkSegmentsSmart(
  segments: string[],
  paragraphMeta: ParagraphMeta[],
  tokenLimit = BATCH_TOKEN_LIMIT
): BatchChunk[] {
  if (segments.length === 0) return [];
  if (paragraphMeta.length === 0) return chunkSegments(segments, tokenLimit);

  const segTokensList = segments.map(estimateTokens);
  const totalTokens = segTokensList.reduce((s, t) => s + t, 0);
  const N = Math.max(1, Math.ceil(totalTokens / tokenLimit));
  const targetPerBatch = totalTokens / N;

  const segToPara = buildSegToParaMap(paragraphMeta, segments.length);
  const listStreakIds = markListStreaks(paragraphMeta);

  // Cut at boundary i = split between segments[i-1] and segments[i].
  // Returns true if cut is bad → chunker skips this boundary, accumulates further.
  function isBadCut(i: number): boolean {
    if (i <= 0 || i >= segments.length) return false;
    const prevPara = segToPara[i - 1];
    const nextPara = segToPara[i];
    if (prevPara < 0 || nextPara < 0) return false; // unmapped — defensive, shouldn't happen
    // Same paragraph → cut would split paragraph internally → always bad.
    if (prevPara === nextPara) return true;
    const prev = paragraphMeta[prevPara];
    const next = paragraphMeta[nextPara];
    // 1. Heading orphan: prev paragraph is heading.
    if (prev.headingLevel != null) return true;
    // 2. Mid-drawing/textbox: cut truly inside drawing block (BOTH protected).
    //    Cuts at drawing boundaries (entering/exiting) are allowed.
    if (prev.inDrawing && next.inDrawing) return true;
    // 3. Mid-table: cut truly inside table block (BOTH protected).
    //    Cuts at table boundaries (entering/exiting) are allowed.
    if (prev.inTable && next.inTable) return true;
    // 4. Mid-list streak (only if both in SAME streak).
    const ps = listStreakIds[prevPara];
    const ns = listStreakIds[nextPara];
    if (ps != null && ns != null && ps === ns) return true;
    return false;
  }

  const batches: BatchChunk[] = [];
  let currentBatch: string[] = [];
  let batchStartIdx = 0;
  let currentTokens = 0;

  for (let i = 0; i < segments.length; i++) {
    const segTokens = segTokensList[i];
    const reachedTarget = currentTokens >= targetPerBatch;
    const wouldExceedLimit = currentTokens + segTokens > tokenLimit;
    const hasMoreBatches = batches.length < N - 1;

    if ((reachedTarget || wouldExceedLimit) && hasMoreBatches && currentBatch.length > 0) {
      if (!isBadCut(i)) {
        batches.push({ startIdx: batchStartIdx, segments: [...currentBatch] });
        currentBatch = [];
        batchStartIdx = i;
        currentTokens = 0;
      }
      // else: bad cut — keep accumulating until safe boundary or end of segments
    }
    currentBatch.push(segments[i]);
    currentTokens += segTokens;
  }

  if (currentBatch.length > 0) {
    batches.push({ startIdx: batchStartIdx, segments: currentBatch });
  }

  // Edge case: if every cut was bad (e.g., 100% table file) we may end up with
  // fewer batches than N target and a single batch exceeding tokenLimit. Log
  // for visibility; do NOT throw — Gemini 2.5 Pro context = 1M tokens absorbs this.
  const overLimit = batches.find(b => b.segments.reduce((s, seg) => s + estimateTokens(seg), 0) > tokenLimit);
  if (overLimit && batches.length < N) {
    console.warn(`[chunkSmart] no safe cut found, accepting over-limit batch (${batches.length}/${N} batches, max ${tokenLimit} per batch)`);
  }

  return batches;
}

export interface IsolatedSegmentRange {
  startIdx: number;
  endIdx: number;
}

/**
 * Run the smart chunker on normal spans while emitting selected contiguous
 * ranges as standalone batches. This changes only API batch boundaries: source
 * segment order/count and DOCX mappings remain untouched.
 *
 * If a requested boundary crosses paragraph metadata, fall back to the normal
 * smart chunker rather than risk splitting an XML-backed paragraph range.
 */
export function chunkSegmentsSmartWithIsolatedRanges(
  segments: string[],
  paragraphMeta: ParagraphMeta[],
  isolatedRanges: IsolatedSegmentRange[],
  tokenLimit = BATCH_TOKEN_LIMIT,
): BatchChunk[] {
  if (segments.length === 0) return [];
  if (isolatedRanges.length === 0) return chunkSegmentsSmart(segments, paragraphMeta, tokenLimit);

  const ranges = isolatedRanges
    .filter(range => Number.isInteger(range.startIdx) && Number.isInteger(range.endIdx))
    .map(range => ({ ...range }))
    .sort((a, b) => a.startIdx - b.startIdx);

  const invalidRange = ranges.length !== isolatedRanges.length
    || ranges.some((range, index) => (
      range.startIdx < 0
      || range.endIdx < range.startIdx
      || range.endIdx >= segments.length
      || (index > 0 && range.startIdx <= ranges[index - 1].endIdx)
    ));
  const crossesParagraph = ranges.some(range => paragraphMeta.some(meta => (
    (meta.segIdxStart < range.startIdx && meta.segIdxEnd >= range.startIdx)
    || (meta.segIdxStart <= range.endIdx && meta.segIdxEnd > range.endIdx)
  )));
  if (invalidRange || crossesParagraph) {
    console.warn('[chunkSmart] invalid isolated range, falling back to normal smart chunking');
    return chunkSegmentsSmart(segments, paragraphMeta, tokenLimit);
  }

  const batches: BatchChunk[] = [];
  const appendNormalSpan = (startIdx: number, endExclusive: number) => {
    if (startIdx >= endExclusive) return;
    const localMeta = paragraphMeta
      .filter(meta => meta.segIdxStart >= startIdx && meta.segIdxEnd < endExclusive)
      .map(meta => ({
        ...meta,
        segIdxStart: meta.segIdxStart - startIdx,
        segIdxEnd: meta.segIdxEnd - startIdx,
      }));
    const localBatches = chunkSegmentsSmart(
      segments.slice(startIdx, endExclusive),
      localMeta,
      tokenLimit,
    );
    batches.push(...localBatches.map(batch => ({
      startIdx: batch.startIdx + startIdx,
      segments: batch.segments,
    })));
  };

  let cursor = 0;
  for (const range of ranges) {
    appendNormalSpan(cursor, range.startIdx);
    batches.push({
      startIdx: range.startIdx,
      segments: segments.slice(range.startIdx, range.endIdx + 1),
    });
    cursor = range.endIdx + 1;
  }
  appendNormalSpan(cursor, segments.length);
  return batches;
}

/**
 * Merge ket qua translate tu nhieu pending_sync rows thanh 1 mang day du.
 * Moi row co { batch_index, result: { translated: string[] } }.
 *
 * Position-aware merge — fix bug "lưu batch 3 vào vị trí batch 1":
 *   Khi 1 batch lỗi/missing, offset của các batch sau vẫn cần đúng.
 *   Pass `batchSizes` (expected segment count per batch_index) để compute offset đúng.
 *   Slot không có result → để empty string '' (caller có thể detect missing để retry).
 */
export function mergeTranslateResults(
  syncRows: { batch_index: number; result: { translated?: string[] } }[],
  totalSegments: number,
  batchSizes?: number[]
): string[] {
  const finalResult = new Array(totalSegments).fill('');

  if (batchSizes && batchSizes.length > 0) {
    // Position-aware: dùng batch_index + expected sizes để compute offset đúng,
    // không phụ thuộc translated.length thực tế (có thể thiếu segment).
    let offset = 0;
    for (let bi = 0; bi < batchSizes.length; bi++) {
      const row = syncRows.find(r => r.batch_index === bi);
      if (row) {
        const translated = row.result?.translated || [];
        const len = Math.min(translated.length, batchSizes[bi]);
        for (let i = 0; i < len; i++) {
          if (offset + i < finalResult.length) {
            finalResult[offset + i] = translated[i];
          }
        }
      }
      offset += batchSizes[bi];
    }
    return finalResult;
  }

  // Backward compat: caller chưa truyền batchSizes → assume all batches present.
  const sorted = [...syncRows].sort((a, b) => a.batch_index - b.batch_index);
  let offset = 0;
  for (const row of sorted) {
    const translated = row.result?.translated || [];
    for (let i = 0; i < translated.length; i++) {
      if (offset + i < finalResult.length) {
        finalResult[offset + i] = translated[i];
      }
    }
    offset += translated.length;
  }

  return finalResult;
}
